import { Monitor, MonitorResult, Incident, AlertContact, StatusPage, MaintenanceWindow, Team, TeamMember, APIKey, CreateMonitorRequest, Plan, CurrentPlan, AdminUser } from '../types';
import { traceError } from '../lib/trace';

const API_BASE = '/api';

interface RequestOptions extends RequestInit {
  /**
   * Whether a 401 should send the browser to /login. Defaults to true, but
   * probes that legitimately 401 when signed out (getMe, getProviders) must
   * opt out: a hard redirect on their response reloads the page, and on
   * /login itself that becomes a reload loop.
   */
  redirectOnUnauthorized?: boolean;
}

async function request<T>(path: string, options?: RequestOptions): Promise<T> {
  const { redirectOnUnauthorized = true, ...init } = options ?? {};
  const method = init.method ?? 'GET';
  const url = `${API_BASE}${path}`;

  let res: Response;
  try {
    res = await fetch(url, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...init.headers,
      },
      credentials: 'include',
    });
  } catch (cause) {
    traceError('api', 'network request failed', { method, url, cause });
    throw new Error(`Could not reach the server (${method} ${url})`);
  }

  if (res.status === 401) {
    traceError('api', 'unauthorized', { method, url, redirectOnUnauthorized });
    if (redirectOnUnauthorized) {
      window.location.href = '/login';
    }
    throw new Error('Unauthorized');
  }

  const body = await res.text().catch(() => '');

  if (!res.ok) {
    traceError('api', 'request failed', {
      method,
      url,
      status: res.status,
      contentType: res.headers.get('content-type'),
      body: body.slice(0, 500),
    });
    throw new Error(errorMessage(body, res.status));
  }

  try {
    return JSON.parse(body) as T;
  } catch (cause) {
    // An HTML body here means something upstream answered instead of the API
    // — typically the SPA fallback catching /api/* before the reverse proxy.
    traceError('api', 'response was not JSON', {
      method,
      url,
      contentType: res.headers.get('content-type'),
      body: body.slice(0, 500),
      cause,
    });
    throw new Error(
      body.trimStart().startsWith('<')
        ? `${method} ${url} returned HTML instead of JSON — /api is not reaching the backend`
        : `${method} ${url} returned a malformed response`
    );
  }
}

function errorMessage(body: string, status: number): string {
  try {
    const parsed = JSON.parse(body);
    if (parsed?.error) {
      return parsed.error;
    }
  } catch {
    if (body.trimStart().startsWith('<')) {
      return `Request failed: ${status} — the server returned HTML instead of JSON`;
    }
  }
  return `Request failed: ${status}`;
}

// Auth
export const authAPI = {
  getProviders: () =>
    request<{ providers: string[] }>('/auth/providers', { redirectOnUnauthorized: false }),
  getMe: () =>
    request<{ user_id: string; team_id: string; email: string; is_admin: boolean }>('/auth/me', {
      redirectOnUnauthorized: false,
    }),
  logout: () => request<{ message: string }>('/auth/logout', { method: 'POST' }),
};

// Monitors
export const monitorsAPI = {
  list: () => request<Monitor[]>('/monitors'),
  get: (id: string) => request<Monitor>(`/monitors/${id}`),
  create: (data: CreateMonitorRequest) =>
    request<Monitor>('/monitors', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: CreateMonitorRequest) =>
    request<Monitor>(`/monitors/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) =>
    request<{ message: string }>(`/monitors/${id}`, { method: 'DELETE' }),
  pause: (id: string) =>
    request<{ message: string }>(`/monitors/${id}/pause`, { method: 'POST' }),
  resume: (id: string) =>
    request<{ message: string }>(`/monitors/${id}/resume`, { method: 'POST' }),
  getResults: (id: string, limit = 100, offset = 0) =>
    request<{ results: MonitorResult[]; total_count: number; has_more: boolean }>(
      `/monitors/${id}/results?limit=${limit}&offset=${offset}`
    ),
};

// Incidents
export const incidentsAPI = {
  list: (limit = 50, offset = 0) =>
    request<{ incidents: Incident[]; total_count: number; has_more: boolean }>(
      `/incidents?limit=${limit}&offset=${offset}`
    ),
};

// Alert Contacts
export const alertContactsAPI = {
  list: () => request<AlertContact[]>('/alert-contacts'),
  create: (data: Partial<AlertContact>) =>
    request<AlertContact>('/alert-contacts', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<AlertContact>) =>
    request<AlertContact>(`/alert-contacts/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) =>
    request<{ message: string }>(`/alert-contacts/${id}`, { method: 'DELETE' }),
  test: (id: string) =>
    request<{ message: string }>(`/alert-contacts/${id}/test`, { method: 'POST' }),
};

// Status Pages
export const statusPagesAPI = {
  list: () => request<StatusPage[]>('/status-pages'),
  get: (id: string) => request<StatusPage>(`/status-pages/${id}`),
  create: (data: Partial<StatusPage>) =>
    request<StatusPage>('/status-pages', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<StatusPage>) =>
    request<StatusPage>(`/status-pages/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) =>
    request<{ message: string }>(`/status-pages/${id}`, { method: 'DELETE' }),
  setMonitors: (id: string, monitorIds: string[]) =>
    request<{ message: string }>(`/status-pages/${id}/monitors`, {
      method: 'PUT',
      body: JSON.stringify({ monitor_ids: monitorIds }),
    }),
};

// Maintenance Windows
export const maintenanceAPI = {
  list: () => request<MaintenanceWindow[]>('/maintenance-windows'),
  create: (data: Partial<MaintenanceWindow>) =>
    request<MaintenanceWindow>('/maintenance-windows', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    request<{ message: string }>(`/maintenance-windows/${id}`, { method: 'DELETE' }),
};

// Team
export const teamAPI = {
  get: () => request<Team>('/team'),
  listMembers: () => request<TeamMember[]>('/team/members'),
  inviteMember: (email: string, role: string) =>
    request<{ message: string }>('/team/members', {
      method: 'POST',
      body: JSON.stringify({ email, role }),
    }),
  removeMember: (userId: string) =>
    request<{ message: string }>(`/team/members/${userId}`, { method: 'DELETE' }),
};

// API Keys
export const apiKeysAPI = {
  list: () => request<APIKey[]>('/api-keys'),
  create: (name: string, scopes: string[]) =>
    request<APIKey & { key: string }>('/api-keys', {
      method: 'POST',
      body: JSON.stringify({ name, scopes }),
    }),
  delete: (id: string) =>
    request<{ message: string }>(`/api-keys/${id}`, { method: 'DELETE' }),
};

// Public Status Page
export const publicAPI = {
  getStatusPage: (slug: string) =>
    fetch(`/status/${slug}`).then(r => r.json()),
};

// Plans
export const plansAPI = {
  list: () => request<{ plans: Plan[] }>('/plans', { redirectOnUnauthorized: false }),
  current: () => request<CurrentPlan>('/plan'),
  change: (plan: string) =>
    request<{ plan: Plan }>('/plan', { method: 'PUT', body: JSON.stringify({ plan }) }),
};

// Platform administration
export const adminAPI = {
  listUsers: () => request<{ users: AdminUser[] }>('/admin/users'),
  deleteUser: (id: string, cascade = false) =>
    request<{ message: string }>(`/admin/users/${id}${cascade ? '?cascade=true' : ''}`, {
      method: 'DELETE',
    }),
  setTeamPlan: (teamId: string, plan: string) =>
    request<{ plan: Plan }>(`/admin/teams/${teamId}/plan`, {
      method: 'PUT',
      body: JSON.stringify({ plan }),
    }),
};
